import React, { useState, useRef, useEffect } from 'react';
import { Mail, KeyRound, ArrowLeft, User, Lock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';
import LandingNavBar from '../components/LandingNavBar';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: ''
  });
  
  // Verification code state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [verificationUsername, setVerificationUsername] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationExpiry, setVerificationExpiry] = useState(5);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRefs = useRef([]);
  
  // Session conflict state
  const [sessionConflict, setSessionConflict] = useState(null);
  const [forceLoginLoading, setForceLoginLoading] = useState(false);
  const [isForceLogin, setIsForceLogin] = useState(false);

  // Forgot password state
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetStep, setResetStep] = useState('request'); // 'request' | 'verify' | 'done'
  const [resetUsername, setResetUsername] = useState('');
  const [resetCode, setResetCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const resetCodeRefs = useRef([]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    if (!resetUsername.trim()) {
      toast.error('Please enter your username');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { username: resetUsername });
      if (res.data.success) {
        setMaskedEmail(res.data.masked_email || '***');
        setResetStep('verify');
        toast.success('Reset code sent to your email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const code = resetCode.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { username: resetUsername, code, new_password: newPassword });
      if (res.data.success) {
        setResetStep('done');
        toast.success('Password reset successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResetCodeInput = (index, value) => {
    if (value.length > 1) value = value[0];
    const newCode = [...resetCode];
    newCode[index] = value;
    setResetCode(newCode);
    if (value && index < 5) resetCodeRefs.current[index + 1]?.focus();
  };

  const handleResetCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !resetCode[index] && index > 0) {
      resetCodeRefs.current[index - 1]?.focus();
    }
  };

  const exitForgotPassword = () => {
    setForgotPasswordMode(false);
    setResetStep('request');
    setResetUsername('');
    setResetCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setMaskedEmail('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSessionConflict(null);

    try {
      if (isLogin) {
        // Login flow
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password
        });

        if (response.data.requires_verification) {
          // Show verification code input
          setVerificationUsername(response.data.username);
          setVerificationEmail(response.data.email);
          setVerificationExpiry(response.data.expires_in_minutes || 5);
          setShowVerification(true);
          setResendCooldown(60);
          toast.success('Verification code sent to your email');
        } else if (response.data.session_conflict) {
          // Show session conflict dialog
          setSessionConflict(response.data);
        } else if (response.data.token) {
          // Direct login (email service unavailable or no verification needed)
          const d = response.data.user ? { ...response.data.user, token: response.data.token, session_id: response.data.session_id } : response.data;
          handleLoginSuccess(d);
        }
      } else {
        // Registration flow
        const response = await axios.post(`${API}/auth/register`, {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name
        });

        if (response.data) {
          setRegistrationSuccess(true);
          toast.success('Registration successful! Please wait for admin approval.');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (data) => {
    // API returns user data at root level, not nested under .user
    const user = {
      id: data.id,
      email: data.email,
      username: data.username,
      full_name: data.full_name,
      role: data.role,
      status: data.status,
      avatar_url: data.avatar_url,
      slogan: data.slogan,
      date_of_birth: data.date_of_birth,
      permissions: data.permissions
    };
    
    localStorage.setItem('token', data.token);
    localStorage.setItem('tokenExpiry', data.token_expires_in ? (Date.now() + data.token_expires_in * 1000).toString() : '');
    localStorage.setItem('session_id', data.session_id);
    localStorage.setItem('user', JSON.stringify(user));
    
    if (data.session_timeout_minutes) {
      localStorage.setItem('session_timeout', data.session_timeout_minutes.toString());
    }
    if (data.session_warning_minutes) {
      localStorage.setItem('session_warning', data.session_warning_minutes.toString());
    }
    
    onLogin(user);
    toast.success(`Welcome back, ${user.full_name || user.username}!`);
  };

  const handleVerificationSubmit = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-login-code`, {
        username: verificationUsername,
        code: code,
        force_login: isForceLogin
      });

      if (response.data.token) {
        handleLoginSuccess(response.data);
      } else {
        toast.error('Login failed - no token received');
      }
    } catch (error) {
      console.error('Verification error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Invalid verification code';
      toast.error(errorMessage);
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const endpoint = isForceLogin ? `${API}/auth/force-login` : `${API}/auth/login`;
      await axios.post(endpoint, {
        username: formData.username,
        password: formData.password
      });
      setResendCooldown(60);
      // Clear old code inputs
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
      toast.success('NEW code sent! Check your email for the latest code.', {
        duration: 5000,
        icon: '📧'
      });
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogin = async () => {
    setForceLoginLoading(true);
    try {
      const response = await axios.post(`${API}/auth/force-login`, {
        username: formData.username,
        password: formData.password
      });

      if (response.data.token) {
        // Direct login (email service unavailable, skip verification)
        const d = response.data.user ? { ...response.data.user, token: response.data.token, session_id: response.data.session_id } : response.data;
        handleLoginSuccess(d);
      } else if (response.data.requires_verification) {
        setVerificationUsername(response.data.username);
        setVerificationEmail(response.data.email);
        setVerificationExpiry(response.data.expires_in_minutes || 5);
        setShowVerification(true);
        setIsForceLogin(true);
        setSessionConflict(null);
        setResendCooldown(60);
        toast.success('NEW verification code sent! Check your email for the latest code.', {
          duration: 6000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force login');
    } finally {
      setForceLoginLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && verificationCode.join('').length === 6) {
      handleVerificationSubmit();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setVerificationCode(newCode);
    if (pastedData.length === 6) {
      codeInputRefs.current[5]?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO 
        title="Sign In - Hotel Management Dashboard"
        description="Sign in to StayMii hotel management dashboard. Access your property management system, bookings, and analytics."
        keywords="staymii login, hotel pms login, hotel management dashboard"
        canonicalUrl="/login"
        noIndex={true}
      />
      {/* Navigation */}
      <LandingNavBar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#0F172A] to-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {showVerification ? 'Verify Your Login' : isLogin ? 'Sign In' : 'Create Account'}
          </h1>
          <p className="text-xl text-white/90 font-medium mb-2">
            {showVerification ? 'Enter the verification code sent to your email' : isLogin ? 'Access your StayMii\u2122 dashboard' : 'Join StayMii\u2122 today'}
          </p>
          <p className="text-lg text-white/70">
            {showVerification ? verificationEmail : isLogin ? 'Manage properties, sync with OTAs, and automate e-invoicing' : 'Start managing your hotel with ease'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="flex-1 py-12 lg:py-16 bg-slate-50">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          
          {/* Session Conflict Dialog */}
          {sessionConflict && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Active Session Detected</h3>
                <p className="text-slate-600 mb-6">{sessionConflict.message}</p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleForceLogin}
                    disabled={forceLoginLoading}
                    className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                  >
                    {forceLoginLoading ? 'Processing...' : 'Force Login Here'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSessionConflict(null)}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Registration Success */}
          {registrationSuccess && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Registration Successful!</h3>
              <p className="text-slate-600 mb-6">Your account has been created. Please wait for admin approval before logging in.</p>
              <Button
                onClick={() => {
                  setRegistrationSuccess(false);
                  setIsLogin(true);
                }}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              >
                Back to Sign In
              </Button>
            </div>
          )}

          {/* Verification Code Form */}
          {showVerification && !sessionConflict && !registrationSuccess && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#2563EB]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="h-8 w-8 text-[#2563EB]" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Enter Verification Code</h2>
                <p className="text-slate-600 text-sm">Code sent to {verificationEmail}</p>
                <p className="text-slate-500 text-xs mt-1">Expires in {verificationExpiry} minutes</p>
              </div>

              {/* Important notice for using latest code */}
              {isForceLogin && (
                <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-amber-800 text-sm text-center font-medium">
                    ⚠️ A NEW code was sent. Use the code from your <span className="underline">most recent</span> email.
                  </p>
                </div>
              )}

              <div className="flex justify-center gap-2 mb-6">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => codeInputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={index === 0 ? handleCodePaste : undefined}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all"
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>

              <Button
                onClick={handleVerificationSubmit}
                disabled={loading || verificationCode.join('').length !== 6}
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold rounded-xl mb-4"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>

              <div className="text-center">
                <button
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || loading}
                  className="text-[#2563EB] hover:text-[#1D4ED8] text-sm font-medium disabled:text-slate-400"
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <button
                  onClick={() => {
                    setShowVerification(false);
                    setVerificationCode(['', '', '', '', '', '']);
                    setIsForceLogin(false);
                  }}
                  className="text-slate-500 hover:text-slate-700 text-sm font-medium inline-flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </button>
              </div>
            </div>
          )}

          {/* Login/Register Form */}
          {!showVerification && !sessionConflict && !registrationSuccess && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#2563EB]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {isLogin ? <Lock className="h-8 w-8 text-[#2563EB]" /> : <User className="h-8 w-8 text-[#2563EB]" />}
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">
                  {isLogin ? 'Welcome Back' : 'Create Your Account'}
                </h2>
                <p className="text-slate-600 text-sm">
                  {isLogin ? 'Enter your credentials to continue' : 'Fill in your details to get started'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {!isLogin && (
                  <div>
                    <Label htmlFor="full_name" className="text-slate-700 font-medium">Full Name</Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Doe"
                      required={!isLogin}
                      className="mt-2 h-12 border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20 rounded-xl bg-white text-slate-900"
                      data-testid="fullname-input"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="username" className="text-slate-700 font-medium">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter your username"
                    required
                    className="mt-2 h-12 border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20 rounded-xl bg-white text-slate-900"
                    data-testid="username-input"
                  />
                </div>

                {!isLogin && (
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@hotel.com"
                      required={!isLogin}
                      className="mt-2 h-12 border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20 rounded-xl bg-white text-slate-900"
                      data-testid="email-input"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="mt-2 h-12 border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20 rounded-xl bg-white text-slate-900"
                    data-testid="password-input"
                  />
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => { setForgotPasswordMode(true); setResetUsername(formData.username); }}
                      className="mt-2 text-[#2563EB] hover:text-[#1D4ED8] text-sm font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>

                {!isLogin && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-blue-700 text-sm text-center">
                      New accounts require admin approval before you can login.
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold rounded-xl flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
                  {!loading && <ArrowRight className="h-5 w-5" />}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setFormData({ username: '', email: '', password: '', full_name: '' });
                  }}
                  className="text-[#2563EB] hover:text-[#1D4ED8] text-sm font-medium"
                  data-testid="toggle-mode-btn"
                >
                  {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-center gap-4 text-sm">
                <Link to="/" className="text-slate-500 hover:text-[#2563EB] transition-colors">
                  ← Back to Home
                </Link>
                <span className="text-slate-300">|</span>
                <Link to="/contact" className="text-slate-500 hover:text-[#2563EB] transition-colors">
                  Contact Support
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Forgot Password Modal */}
        {forgotPasswordMode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="forgot-password-modal">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl">
              {resetStep === 'request' && (
                <form onSubmit={handleForgotPasswordRequest}>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Forgot Password</h3>
                  <p className="text-slate-500 text-sm mb-6">Enter your username and we'll send a reset code to your email.</p>
                  <Label htmlFor="reset-username" className="text-slate-700 font-medium">Username</Label>
                  <Input
                    id="reset-username"
                    value={resetUsername}
                    onChange={(e) => setResetUsername(e.target.value)}
                    placeholder="Enter your username"
                    className="mt-2 mb-6 h-12 border-slate-200 focus:border-[#2563EB] rounded-xl"
                    data-testid="reset-username-input"
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={exitForgotPassword} className="flex-1 h-12 rounded-xl" data-testid="cancel-reset-btn">Cancel</Button>
                    <Button type="submit" disabled={loading} className="flex-1 h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl" data-testid="send-reset-code-btn">
                      {loading ? 'Sending...' : 'Send Reset Code'}
                    </Button>
                  </div>
                </form>
              )}
              {resetStep === 'verify' && (
                <form onSubmit={handleResetPassword}>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Reset Password</h3>
                  <p className="text-slate-500 text-sm mb-6">Enter the 6-digit code sent to <span className="font-medium text-slate-700">{maskedEmail}</span></p>
                  <div className="flex gap-2 justify-center mb-6">
                    {resetCode.map((digit, i) => (
                      <Input
                        key={i}
                        ref={el => resetCodeRefs.current[i] = el}
                        value={digit}
                        onChange={e => handleResetCodeInput(i, e.target.value)}
                        onKeyDown={e => handleResetCodeKeyDown(i, e)}
                        maxLength={1}
                        className="w-12 h-14 text-center text-xl font-bold border-slate-200 focus:border-[#2563EB] rounded-xl"
                        data-testid={`reset-code-${i}`}
                      />
                    ))}
                  </div>
                  <div className="space-y-3 mb-6">
                    <div>
                      <Label className="text-slate-700 font-medium">New Password</Label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="Min 6 characters"
                        className="mt-1 h-12 border-slate-200 focus:border-[#2563EB] rounded-xl"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">Confirm Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        className="mt-1 h-12 border-slate-200 focus:border-[#2563EB] rounded-xl"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={exitForgotPassword} className="flex-1 h-12 rounded-xl" data-testid="cancel-reset-btn-2">Cancel</Button>
                    <Button type="submit" disabled={loading} className="flex-1 h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl" data-testid="reset-password-btn">
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </div>
                </form>
              )}
              {resetStep === 'done' && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Password Reset!</h3>
                  <p className="text-slate-500 text-sm mb-6">Your password has been reset successfully. You can now sign in with your new password.</p>
                  <Button onClick={exitForgotPassword} className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl" data-testid="back-to-login-btn">
                    Back to Sign In
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="py-6 text-center" style={{ backgroundColor: '#192336' }}>
        <div className="flex items-center justify-center gap-4 mb-4">
          <Link to="/terms-of-service" className="text-white/70 text-sm hover:text-white transition-colors">Terms of Service</Link>
          <span className="text-white/50">|</span>
          <Link to="/privacy-policy" className="text-white/70 text-sm hover:text-white transition-colors">Privacy Policy</Link>
          <span className="text-white/50">|</span>
          <Link to="/contact" className="text-white/70 text-sm hover:text-white transition-colors">Contact Us</Link>
        </div>
        <p className="text-sm text-gray-400">Powered by Mii Global | StayMii&trade; Platform</p>
        <p className="text-white/50 text-xs">© {new Date().getFullYear()} StayMii – A Product by Mii Global</p>
      </footer>
    </div>
  );
};

export default Login;
